import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../../../../components/card/card.component';
import { useShared } from '../../../../contexts/shared.context';
import { IBudgetInvestment, ICardName } from '../../../../types/kato.types';
import { useKato } from '../../../../contexts/kato.context';
import classes from './styles.module.scss';
import { monthsRu } from '../../../../consts/other.conts';
import { useNavigate, useParams } from 'react-router-dom';

const translationKey = 'cards.budget-investment-projects-card';
const baseClass = 'budget-investment-projects-card';
const additionalClass = 'budget-investment-projects-card';

const budgetRegions: IBudgetInvestment['typeBudget'][] = [
  'REGIONAL_BUDGET',
  'DEDICATED_REGIONAL_BUDGET',
];

const BudgetInvestmentProjectsCard = () => {
  const { t } = useTranslation();
  const { setFullscreen, selectedInfoYear } = useShared();
  const { budgetInvestment, budgetInvIframe } = useKato().katoGeneralInfo;
  const navigate = useNavigate();
  const { kato, link, tab } = useParams<{
    kato: string;
    link: string;
    tab: string;
  }>();

  const [currentType, setCurrentType] =
    useState<IBudgetInvestment['typeBudget']>('REGIONAL_BUDGET');
  const [data, setData] = useState<IBudgetInvestment[]>([]);

  const headers = [
    {
      title: 'plan',
      subtitle: t(`${translationKey}.table.month`, {
        month: monthsRu[new Date().getMonth()],
      }),
      calculate: false,
    },
    {
      title: 'fact',
      calculate: false,
    },
    {
      title: 'percentage',
      calculate: true,
    },
    {
      title: 'plan2024',
      subtitle: t(`${translationKey}.table.year`, { year: selectedInfoYear }),
      calculate: false,
    },
    {
      title: 'percentage2024',
      calculate: true,
    },
  ];

  const onClick = () => {
    setFullscreen({ open: true, type: ICardName.BUDGET_INVESTMENT_PROJECTS });
    navigate(`/${kato}/${link}/${tab}/${ICardName.BUDGET_INVESTMENT_PROJECTS}`);
  };

  useEffect(() => {
    if (!budgetInvestment || !budgetInvestment.length) return;
    setData(budgetInvestment.filter((item) => item.typeBudget === currentType));
  }, [currentType, budgetInvestment]);

  const renderRow = (filterKey: 'tek_budget' | 'budget_razv') => {
    const rowItems = headers.map(({ title, calculate }, i) => {
      const item = data.find((d) => d.devType === filterKey);
      if (!item) return <td key={i}>-</td>;

      let value: number | string = item[
        title as keyof IBudgetInvestment
      ] as number;
      if (calculate) {
        if (title === 'percentage') {
          value = ((item.fact / item.plan) * 100).toFixed(2) + '%';
        } else if (title === 'percentage2024') {
          value = ((item.fact / item.plan2024) * 100).toFixed(2) + '%';
        }
      } else {
        value = ((value as number) / 1_000_000).toFixed(2);
      }

      return <td key={i}>{value}</td>;
    });

    return (
      <tr>
        <td>{t(`${translationKey}.table.${filterKey}`)}</td>
        {rowItems}
      </tr>
    );
  };

  const renderTotalRow = () => {
    const sums = headers.map(({ title, calculate }) => {
      const total = data.reduce((acc, cur) => {
        const val = cur[title as keyof IBudgetInvestment];
        return acc + (typeof val === 'number' ? val : 0);
      }, 0);

      if (calculate) {
        const totalFact = data.reduce((acc, cur) => acc + cur.fact, 0);
        const totalPlan =
          title === 'percentage'
            ? data.reduce((acc, cur) => acc + cur.plan, 0)
            : data.reduce((acc, cur) => acc + cur.plan2024, 0);
        const res = (totalFact / totalPlan) * 100;
        if (isNaN(res)) return '0.00%';
        return ((totalFact / totalPlan) * 100).toFixed(2) + '%';
      }

      return (total / 1_000_000).toFixed(2);
    });

    return (
      <tr>
        <td>{t('total')}</td>
        {sums.map((val, i) => (
          <td key={i}>{val}</td>
        ))}
      </tr>
    );
  };

  return (
    <Card
      title={t(`${translationKey}.title`)}
      // subtitle={t(`${translationKey}.subtitle`)}
      onClick={onClick}
    >
      {budgetInvIframe?.iframeUrlExp ? (
        <div className={classes[`${additionalClass}__iframe`]}>
          <iframe
            src={budgetInvIframe?.iframeUrlExp}
            width="100%"
            height="100%"
          ></iframe>
        </div>
      ) : (
        <div className={classes[baseClass]}>
          <div className={classes[`${baseClass}__radios`]}>
            {budgetRegions.map((type) => (
              <label
                key={type}
                className={`${currentType === type ? classes['active'] : ''}`}
              >
                <input
                  type="radio"
                  name="tax-type"
                  checked={currentType === type}
                  onChange={() => setCurrentType(type)}
                />
                {t(`${translationKey}.${type}`)}
              </label>
            ))}
          </div>
          <div className={classes[`${baseClass}__table`]}>
            <table>
              <thead>
                <tr>
                  <th></th>
                  {headers.map((header, index) => (
                    <th key={index}>
                      <div className={classes[`${baseClass}__header`]}>
                        <span>
                          {t(`${translationKey}.table.${header.title}`)}
                        </span>
                        {header.subtitle && <span>{header.subtitle}</span>}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {renderTotalRow()}
                {renderRow('tek_budget')}
                {renderRow('budget_razv')}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
};

export default BudgetInvestmentProjectsCard;
